import type { Theme, SxProps, Breakpoint } from '@mui/material/styles';

import { useEffect } from 'react';

import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import { useTheme } from '@mui/material/styles';
import ListItemButton from '@mui/material/ListItemButton';
import Drawer, { drawerClasses } from '@mui/material/Drawer';

import { usePathname } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import {  createLinearGradient, hexToRgbChannel, varAlpha } from 'src/theme/styles';

import { Scrollbar } from 'src/components/scrollbar';
import { Card, Typography } from '@mui/material';
import { Iconify } from 'src/components/iconify';



// ----------------------------------------------------------------------

export type NavContentProps = {
  data: {
    path: string;
    title: string;
    icon: string;
    info?: React.ReactNode;
  }[];
  slots?: {
    topArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
  sx?: SxProps<Theme>;
};

export function NavDesktop({
  sx,
  data,
  slots,
  layoutQuery,
}: NavContentProps & { layoutQuery: Breakpoint }) {
  const theme = useTheme();

  return (
    <Card
    elevation={8}
      sx={{
        pt: 2.5,
        px: 1,
        top: 70,
        m:2,
        borderRadius:2,
        left: 0,
        display: 'none',
        position: 'fixed',
        height: 'calc(100% - 100px)',
        flexDirection: 'column',
        bgcolor: 'var(--layout-nav-bg)',
        zIndex: 'var(--layout-nav-zIndex)',
        width: 'var(--layout-nav-vertical-width)',
        borderRight: `1px solid var(--layout-nav-border-color, ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)})`,
        [theme.breakpoints.up(layoutQuery)]: {
          display: 'flex',
          backgroundImage: createLinearGradient(hexToRgbChannel(theme.palette.primary.light,true),hexToRgbChannel(theme.palette.primary.light,true), 0.4, 0.2, 180)
        },
        ...sx,
      }}
    >
      <NavContent data={data} slots={slots}  />
    </Card>
  );
}

export function NavMobile({
  sx,
  data,
  open,
  slots,
  onClose,
}: NavContentProps & { open: boolean; onClose: () => void }) {
  const pathname = usePathname();

  useEffect(() => {
    if (open) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      sx={{
        [`& .${drawerClasses.paper}`]: {
          pt: 2.5,
          px: 2.5,
          overflow: 'unset',
          bgcolor: 'var(--layout-nav-bg)',
          width: 'var(--layout-nav-mobile-width)',
          ...sx,
        },
      }}
    >
      <NavContent data={data} slots={slots} />
    </Drawer>
  );
}


export function NavContent({ data, slots, sx }: NavContentProps) {
  const pathname = usePathname();

  const handleNavigation = (path: string) => (event: React.MouseEvent) => {
    if (path === pathname) {
      event.preventDefault();
      window.location.href = path;
    }
  };

  return (
    <>
      {slots?.topArea}

      <Scrollbar fillContent 
        style={{
          overflowX:'hidden'
        }} >
        <Box component="nav" display="flex" flex="1 1 auto" flexDirection="column" sx={sx}>
          <Box component="ul" gap={1} display="flex" flexDirection="column" style={{paddingInlineStart:0,marginTop:0}}>
            {data.map((item) => {
              const isActived = item.path === pathname;

              return (
                <ListItem disableGutters disablePadding key={item.title}>
                  <ListItemButton
                    disableGutters
                    component={RouterLink}
                    href={item.path}
                    onClick={handleNavigation(item.path)}
                    sx={{
                      pl: 2,
                      py: 1,
                      gap: {xs:2, md:1},
                      pr: 1.5,
                      borderRadius: 0.75,
                      typography: 'body2',
                      fontWeight: 'fontWeightMedium',
                      color: 'var(--layout-nav-item-color)',
                      bgcolor: 'var(--layout-nav-item-bg)',
                      minHeight: 'var(--layout-nav-item-height)',
                      backdropFilter: 'blur(2px)',
                      WebkitBackdropFilter: 'blur(2px)',
                      ...(isActived && {
                        fontWeight: 'fontWeightSemiBold',
                        bgcolor: 'var(--layout-nav-item-active-bg)',
                        color: 'var(--layout-nav-item-active-color)',
                      }),
                      display:'flex',
                      flexDirection:{xs:'row', md:'column'}
                    }}
                  >
                    <Iconify width={24} icon={item.icon} />
                    <Typography textAlign={{xs:'left', md:'center'}} fontWeight={800} fontSize={10} variant={'caption'} flexGrow={1}>
                      {item.title.toUpperCase()}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </Box>
        </Box>
      </Scrollbar>

      {slots?.bottomArea}
    </>
  );
}
